import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { format, parseISO } from 'date-fns'
import {
  Loader2, X, Edit2, Trash2, Package2,
  ArrowDownToLine, ArrowUpFromLine, RotateCcw, Clock,
  ChevronUp, ChevronDown, ChevronsUpDown, Container, AlertTriangle, ShoppingCart} from 'lucide-react'
import {
  getAllWarehouseActivities,
  updateWarehouseActivity,
  deleteWarehouseActivity,
  getAdminClients,
} from '../../lib/api'
import { ToastContainer, useToast } from '../../components/Toast'

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatCurrency(v) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  }).format(v ?? 0)
}

function formatDate(str) {
  if (!str) return '—'
  try { return format(parseISO(str), 'dd/MM/yy') } catch { return str }
}

const ACTIVITY_TYPES = [
  { value: 'goods_in',   label: 'Goods In',         icon: ArrowDownToLine, color: 'text-green-600',  bg: 'bg-green-50'  },
  { value: 'package_in', label: 'Package Received',  icon: Package2,        color: 'text-blue-600',   bg: 'bg-blue-50'   },
  { value: 'return',     label: 'Return',            icon: RotateCcw,       color: 'text-amber-600',  bg: 'bg-amber-50'  },
  { value: 'goods_out',  label: 'Goods Out',         icon: ArrowUpFromLine, color: 'text-purple-600', bg: 'bg-purple-50' },
  { value: 'time',       label: 'Time',              icon: Clock,           color: 'text-slate-600',  bg: 'bg-slate-100' },
  { value: 'container_20ft', label: '20ft Container', icon: Container,       color: 'text-teal-600',   bg: 'bg-teal-50'   },
  { value: 'container_40ft', label: '40ft Container', icon: Container,       color: 'text-cyan-700',   bg: 'bg-cyan-50'   },
  { value: 'purchase',   label: 'Purchase (cost)',   icon: ShoppingCart,    color: 'text-indigo-600', bg: 'bg-indigo-50' },
]

function activityMeta(type) {
  return ACTIVITY_TYPES.find(t => t.value === type) || { label: type, icon: Package2, color: 'text-slate-500', bg: 'bg-slate-100' }
}

// ── Sort icon ─────────────────────────────────────────────────────────────────

function SortIcon({ col, sortCol, sortDir }) {
  if (sortCol !== col) return <ChevronsUpDown size={11} className="text-slate-300 ml-1 inline-block flex-shrink-0" />
  return sortDir === 'asc'
    ? <ChevronUp   size={11} className="text-blue-500 ml-1 inline-block flex-shrink-0" />
    : <ChevronDown size={11} className="text-blue-500 ml-1 inline-block flex-shrink-0" />
}

// ── Edit modal ────────────────────────────────────────────────────────────────

function EditModal({ activity, onClose, onSuccess }) {
  const [form, setForm] = useState({
    activity_type: activity.activity_type,
    quantity:      String(activity.quantity),
    reference:     activity.reference ?? '',
    notes:         activity.notes ?? '',
    activity_date: activity.activity_date,
    fee:           '',
  })
  const { toasts, addToast, removeToast } = useToast()

  const mutation = useMutation({
    mutationFn: (payload) => updateWarehouseActivity(activity.id, payload),
    onSuccess: () => onSuccess(),
    onError: (err) => addToast({ type: 'error', message: err.response?.data?.detail || 'Failed to save.' }),
  })

  function handleSubmit(e) {
    e.preventDefault()
    mutation.mutate({
      activity_type: form.activity_type,
      quantity:      parseFloat(form.quantity) || 1,
      reference:     form.reference || null,
      notes:         form.notes || null,
      activity_date: form.activity_date,
      fee:           form.fee !== '' ? Number(form.fee) : undefined,
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h3 className="font-semibold text-slate-800">Edit Activity</h3>
            <p className="text-xs text-slate-400 mt-0.5">{activity.client_name}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="label">Type</label>
            <select
              value={form.activity_type}
              onChange={e => setForm(p => ({ ...p, activity_type: e.target.value }))}
              className="input"
            >
              {ACTIVITY_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Quantity</label>
              <input type="number" min="0" step="0.01" value={form.quantity}
                onChange={e => setForm(p => ({ ...p, quantity: e.target.value }))}
                className="input" />
            </div>
            <div>
              <label className="label">Date</label>
              <input type="date" value={form.activity_date}
                onChange={e => setForm(p => ({ ...p, activity_date: e.target.value }))}
                className="input" />
            </div>
          </div>
          <div>
            <label className="label">Reference / Order #</label>
            <input type="text" value={form.reference}
              onChange={e => setForm(p => ({ ...p, reference: e.target.value }))}
              className="input" placeholder="e.g. NV10045" />
          </div>
          <div>
            <label className="label">Notes</label>
            <input type="text" value={form.notes}
              onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              className="input" placeholder="Optional details" />
          </div>
          <div>
            <label className="label">Fee Override ($)</label>
            <input type="number" min="0" step="0.01" value={form.fee}
              onChange={e => setForm(p => ({ ...p, fee: e.target.value }))}
              className="input" placeholder="Leave blank to auto-calculate" />
            <p className="text-xs text-slate-400 mt-1">Blank = auto-calculated from rates</p>
          </div>
          <div className="flex gap-3 justify-end pt-1">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={mutation.isPending}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 size={14} className="animate-spin" />}
              Save Changes
            </button>
          </div>
        </form>
        <ToastContainer toasts={toasts} onClose={removeToast} />
      </div>
    </div>
  )
}

// ── Column definitions ────────────────────────────────────────────────────────

const COLS = [
  { key: 'client_name',    label: 'Client',     align: 'left'  },
  { key: 'activity_date',  label: 'Date',        align: 'left'  },
  { key: 'activity_type',  label: 'Type',        align: 'left'  },
  { key: 'quantity',       label: 'Qty',         align: 'right' },
  { key: 'reference',      label: 'Reference',   align: 'left'  },
  { key: 'notes',          label: 'Notes',       align: 'left'  },
  { key: 'fee',            label: 'Fee',         align: 'right' },
]

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AllActivities() {
  const queryClient = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()

  // Filters
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
  })
  const [toDate, setToDate]         = useState('')
  const [clientFilter, setClientFilter] = useState('')
  const [typeFilter, setTypeFilter]     = useState('')

  // Sort
  const [sortCol, setSortCol] = useState('activity_date')
  const [sortDir, setSortDir] = useState('desc')

  // Edit modal
  const [editing, setEditing] = useState(null)

  const queryParams = {
    ...(fromDate     && { from: fromDate }),
    ...(toDate       && { to: toDate }),
    ...(clientFilter && { client_id: clientFilter }),
    ...(typeFilter   && { activity_type: typeFilter }),
  }

  const { data: activities = [], isLoading, error } = useQuery({
    queryKey: ['warehouse', 'activities', 'all', queryParams],
    queryFn:  () => getAllWarehouseActivities(queryParams),
    refetchInterval: 120_000,
  })

  const { data: clients = [] } = useQuery({
    queryKey: ['admin', 'clients'],
    queryFn:  getAdminClients,
  })
  const sortedClients = [...clients].sort((a, b) => a.name.localeCompare(b.name))

  const deleteMutation = useMutation({
    mutationFn: deleteWarehouseActivity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouse', 'activities', 'all'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] })
      addToast({ type: 'success', message: 'Activity deleted.' })
    },
    onError: () => addToast({ type: 'error', message: 'Failed to delete.' }),
  })

  function handleSort(key) {
    if (sortCol === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortCol(key)
      setSortDir(key === 'activity_date' ? 'desc' : 'asc')
    }
  }

  const sorted = useMemo(() => {
    return [...activities].sort((a, b) => {
      const av = a[sortCol] ?? ''
      const bv = b[sortCol] ?? ''
      let cmp
      if (typeof av === 'number' || typeof bv === 'number') {
        cmp = (Number(av) || 0) - (Number(bv) || 0)
      } else {
        cmp = String(av).localeCompare(String(bv))
      }
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [activities, sortCol, sortDir])

  // Totals
  const grandTotal = activities.reduce((s, a) => s + (a.fee || 0), 0)

  function handleModalSuccess() {
    queryClient.invalidateQueries({ queryKey: ['warehouse', 'activities', 'all'] })
    queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] })
    setEditing(null)
    addToast({ type: 'success', message: 'Activity saved.' })
  }

  return (
    <>
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      <div className="space-y-6">

        {/* Header */}
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Slack Activity Log</h2>
          <p className="text-sm text-slate-500 mt-0.5">All warehouse activities across every client</p>
        </div>

        {/* Filters */}
        <div className="card p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="label">From</label>
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                className="text-xs border border-slate-200 rounded-md px-2 py-1.5 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-400" />
            </div>
            <div>
              <label className="label">To</label>
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                className="text-xs border border-slate-200 rounded-md px-2 py-1.5 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-400" />
            </div>
            <div>
              <label className="label">Client</label>
              <select value={clientFilter} onChange={e => setClientFilter(e.target.value)}
                className="text-xs border border-slate-200 rounded-md px-2 py-1.5 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white">
                <option value="">All clients</option>
                {sortedClients.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Type</label>
              <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
                className="text-xs border border-slate-200 rounded-md px-2 py-1.5 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white">
                <option value="">All types</option>
                {ACTIVITY_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            {(fromDate || toDate || clientFilter || typeFilter) && (
              <button
                onClick={() => { setFromDate(''); setToDate(''); setClientFilter(''); setTypeFilter('') }}
                className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1"
              >
                <X size={12} /> Clear
              </button>
            )}
            {/* On a failed fetch `activities` falls back to [], which would
                read as "0 records / $0.00" rather than an unknown total. */}
            <div className="ml-auto text-right">
              <p className="text-xs text-slate-400">
                {isLoading ? '…' : error ? <span className="text-red-600 font-medium">load failed</span> : `${activities.length} records`}
              </p>
              <p className="text-sm font-semibold text-slate-800 tabular-nums">
                {isLoading ? '—' : error ? <span className="text-red-600">—</span> : formatCurrency(grandTotal)}
              </p>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {COLS.map(col => (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      className={[
                        'px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap',
                        'select-none cursor-pointer hover:text-slate-700 hover:bg-slate-100 transition-colors',
                        col.align === 'right' ? 'text-right' : 'text-left',
                      ].join(' ')}
                    >
                      {col.label}
                      <SortIcon col={col.key} sortCol={sortCol} sortDir={sortDir} />
                    </th>
                  ))}
                  {/* Actions column — not sortable */}
                  <th className="px-3 py-2 w-16" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isLoading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: COLS.length + 1 }).map((_, j) => (
                        <td key={j} className="px-3 py-2">
                          <div className="skeleton h-3 rounded" style={{ width: j === 0 ? '100px' : '60px' }} />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : error ? (
                  // Without this branch the `= []` default sends the operator
                  // to the "no activities found / adjust the filters" state,
                  // so a failed load looks like an empty, fully-billed month.
                  <tr>
                    <td colSpan={COLS.length + 1} className="py-16 text-center">
                      <AlertTriangle size={28} className="mx-auto text-red-400 mb-2" />
                      <p className="text-slate-600 text-sm font-medium">Couldn&rsquo;t load activities</p>
                      <p className="text-slate-400 text-xs mt-1">
                        This is <b>not</b> an empty result — don&rsquo;t adjust the filters, and don&rsquo;t
                        read the total above as $0.
                      </p>
                      <p className="text-red-600 text-xs mt-2 font-mono break-all px-6">
                        {error?.response?.data?.detail || error?.message || 'Unknown error'}
                      </p>
                    </td>
                  </tr>
                ) : sorted.length === 0 ? (
                  <tr>
                    <td colSpan={COLS.length + 1} className="py-16 text-center">
                      <Package2 size={28} className="mx-auto text-slate-300 mb-2" />
                      <p className="text-slate-400 text-sm">No activities found</p>
                      <p className="text-slate-300 text-xs mt-1">Try adjusting the filters</p>
                    </td>
                  </tr>
                ) : sorted.map(a => {
                  const meta = activityMeta(a.activity_type)
                  const Icon = meta.icon
                  return (
                    <tr key={a.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-3 py-2 font-medium text-slate-800 whitespace-nowrap">
                        {a.client_name}
                      </td>
                      <td className="px-3 py-2 text-slate-500 whitespace-nowrap tabular-nums">
                        {formatDate(a.activity_date)}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 font-medium px-2 py-0.5 rounded-full ${meta.bg} ${meta.color}`}>
                          <Icon size={10} />
                          {meta.label}
                          {a.slack_ts && <span className="opacity-40 ml-0.5" title="Synced from Slack">✦</span>}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                        {a.quantity}
                      </td>
                      <td className="px-3 py-2 font-mono text-slate-600">
                        {a.reference || <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2 text-slate-500 max-w-xs truncate">
                        {a.notes || <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-900">
                        {formatCurrency(a.fee)}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setEditing(a)}
                            className="p-1 rounded text-slate-300 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                            title="Edit"
                          >
                            <Edit2 size={13} />
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm('Delete this activity?')) {
                                deleteMutation.mutate(a.id)
                              }
                            }}
                            disabled={deleteMutation.isPending}
                            className="p-1 rounded text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
              {!isLoading && sorted.length > 0 && (
                <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                  <tr>
                    <td colSpan={COLS.length - 1} className="px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">
                      Total ({activities.length} activities)
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-slate-800">
                      {formatCurrency(grandTotal)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

      </div>

      {editing && (
        <EditModal
          activity={editing}
          onClose={() => setEditing(null)}
          onSuccess={handleModalSuccess}
        />
      )}
    </>
  )
}
